import apiClient from "./apiClient";
import publicApiClient from "./publicApiClient";
import { CardInfo } from "types/card";
import { AppConfig } from "types/appConfig";
import { TokenSupport } from "types/tokenSupport";
import { NETWORK_NAME, NetworkType } from "./network";

export async function registerUser(payload: {
  cardNumber: string;
  email: string;
}): Promise<any> {
  // Public endpoint: during signup the user has no session yet, so this must use
  // publicApiClient (no auth header / app token / encryption). Going through
  // apiClient throws "No authenticated user" before the request even leaves, and
  // would also encrypt the body that this public route can't decrypt.
  const res = await publicApiClient.post("/registerUser", payload);
  return res.data;
}

// network is threaded in from the callers (all of them React hooks that already
// read it for other reasons) rather than pulled from a module-level global, so
// these stay pure and the required input is visible at every call site.
export async function getCardInfo(
  cardNumber: string,
  network: NetworkType,
): Promise<CardInfo> {
  const res = await apiClient.get("/getCardInfo", {
    params: {
      cardNumber,
      network_name: NETWORK_NAME,
      network_type: network,
    },
  });
  return res.data;
}

export async function getAppConfig(network: NetworkType): Promise<AppConfig> {
  const res = await apiClient.get("/appConfig", {
    params: {
      network_name: NETWORK_NAME,
      network_type: network,
    },
  });
  return res.data;
}

// `token` undefined => backend uses the contract's default. Axios drops undefined params.
export async function getTokenSupport(
  token: string,
  network: NetworkType,
): Promise<TokenSupport> {
  const res = await apiClient.get("/token/support", {
    params: {
      token,
      network_name: NETWORK_NAME,
      network_type: network,
    },
  });
  return res.data;
}

export async function getCardBalance(
  cardNumber: string,
  network: NetworkType,
  token?: string,
): Promise<number> {
  const res = await apiClient.get("/getCardBalance", {
    params: {
      cardNumber,
      network_name: NETWORK_NAME,
      network_type: network,
      token,
    },
  });
  return res.data;
}

export async function getCardTransactions(
  cardAddress: string,
  network: NetworkType,
  token?: string,
): Promise<any> {
  const res = await apiClient.get("/getCardTransactions", {
    params: {
      cardAddress,
      network_name: NETWORK_NAME,
      network_type: network,
      token,
    },
  });
  return res.data;
}

export async function merchantNameFromAliasAddressApi(
  address: string,
  network: NetworkType,
) {
  return apiClient.get("/merchantNameByAliasAddress", {
    params: {
      merchantAddress: address,
      network_name: NETWORK_NAME,
      network_type: network,
    },
  });
}

// ── Transaction-receipt email: verify, then store ─────────────────────────────
// POST bodies go through apiClient, which encrypts them and attaches the session; the network
// envelope is required because the backend's web routes sit behind deployNetworkGuardWeb.

export interface VerifyEmailPayload {
  email: string;
}

export interface VerifyEmailResponse {
  /** How long the mailed code stays usable. */
  expiresInMs: number;
}

export interface VerifyOtpPayload {
  email: string;
  otp: string;
}

/** Step 1: mail a one-time code to the address the cardholder wants receipts at. */
export async function verifyNotificationEmail(
  payload: VerifyEmailPayload,
  network: NetworkType,
): Promise<VerifyEmailResponse> {
  const res = await apiClient.post("/notifications/email/verify", {
    ...payload,
    network_name: NETWORK_NAME,
    network_type: network,
  });
  return res.data;
}

/** Step 2: submit the code. Marks the address verified for a short window. */
export async function verifyNotificationOtp(
  payload: VerifyOtpPayload,
  network: NetworkType,
): Promise<{ verified: true }> {
  const res = await apiClient.post("/notifications/email/verifyOtp", {
    ...payload,
    network_name: NETWORK_NAME,
    network_type: network,
  });
  return res.data;
}

/** Step 3: store the verified address. The uid comes from the session, never from here. */
export async function setNotificationEmail(
  payload: VerifyEmailPayload,
  network: NetworkType,
): Promise<{ notificationEmail: string }> {
  const res = await apiClient.post("/notifications/email", {
    ...payload,
    network_name: NETWORK_NAME,
    network_type: network,
  });
  return res.data;
}

export async function suspendCardApi(cardNumber: string, network: NetworkType) {
  return apiClient.post("/suspendCard", {
    cardNumber,
    network_name: NETWORK_NAME,
    network_type: network,
  });
}
